import { useState, useEffect } from 'react';
import './App.css';
import InviteInterstitial from './InviteInterstitial';
import ReferralInterstitial from './ReferralInterstitial';
import { storeUrlForUA } from './LiveScoreView';

const APP_STORE_URL = 'https://apps.apple.com/us/app/advanced-bowling-tracker/id6759532374';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.advancedbowlingtracker';
const TOURNAMENTS_URL = 'https://advancedbowlingtournaments.com';
const SUPPORT_EMAIL = 'mailto:support@advancedbowlingtracker.com';

// Platform-aware routing for the generic "Download" CTAs (announcement bar + nav
// button). Android visitors go to Google Play; iOS and desktop default to the App
// Store (iOS is the flagship, and the hero shows both store badges explicitly).
const IS_ANDROID = /android/i.test(navigator.userAgent);
const STORE_URL = IS_ANDROID ? PLAY_STORE_URL : APP_STORE_URL;
const STORE_LABEL = IS_ANDROID ? 'Get it on Google Play' : 'Download on the App Store';

// Check if current path is a deep link
const DEEP_LINK_PATH = window.location.pathname.match(/^\/(profile|invite|session|friend|league|r)\// ) || window.location.pathname === '/startsession'
  ? window.location.pathname
  : null;

// Extract invite code if this is an invite deep link
const INVITE_CODE = window.location.pathname.match(/^\/invite\/([A-Za-z0-9]+)/)?.[1] || null;

// Extract referral code if this is a referral deep link (/r/{6-char code})
const REFERRAL_CODE = window.location.pathname.match(/^\/r\/([A-Za-z0-9]+)/)?.[1] || null;

function DeepLinkInterstitial() {
  // Referral links get the reward-pitch landing page
  if (REFERRAL_CODE) {
    return <ReferralInterstitial code={REFERRAL_CODE} />;
  }
  // For invite links, use the rich invite interstitial
  if (INVITE_CODE) {
    return <InviteInterstitial inviteCode={INVITE_CODE} />;
  }

  const customSchemeURL = 'bowlingtracker:/' + DEEP_LINK_PATH;

  return <GenericDeepLinkInterstitial customSchemeURL={customSchemeURL} />;
}

export function GenericDeepLinkInterstitial({ customSchemeURL }) {
  // Android visitors get Google Play, everyone else the App Store — same
  // routing as ReferralInterstitial and the live-view download buttons.
  const storeUrl = storeUrlForUA(typeof navigator !== 'undefined' ? navigator.userAgent : '');

  useEffect(() => {
    // Try custom URL scheme to open the app automatically
    window.location.href = customSchemeURL;
  }, [customSchemeURL]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: '#0a0e1a', color: '#fff', fontFamily: 'Inter, sans-serif',
      padding: '24px', textAlign: 'center',
    }}>
      <img src={process.env.PUBLIC_URL + '/favicon.png'} alt="ABT" style={{ width: 80, height: 80, borderRadius: 16, marginBottom: 24 }} />
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Opening Advanced Bowling Tracker...</h2>
      <p style={{ fontSize: 15, color: '#999', margin: '0 0 24px' }}>If the app doesn't open automatically:</p>
      <a href={customSchemeURL} style={{
        display: 'inline-block', padding: '14px 32px', background: '#E53935', color: '#fff',
        borderRadius: 12, fontWeight: 600, fontSize: 16, textDecoration: 'none', marginBottom: 16,
      }}>Open in App</a>
      <a href={storeUrl} style={{
        display: 'inline-block', padding: '14px 32px', background: 'transparent', color: '#999',
        borderRadius: 12, fontWeight: 500, fontSize: 14, textDecoration: 'none',
        border: '1px solid #333',
      }}>Get the App</a>
    </div>
  );
}

// App icon used as the wordmark logo (nav, footer, announcement bar).
function AppLogo({ className }) {
  return <img className={className} src={process.env.PUBLIC_URL + '/logo192.png'} alt="" aria-hidden="true" />;
}

// Dark App Store pill (custom, matches the design).
function AppStoreButton({ className = '' }) {
  return (
    <a className={`appstore ${className}`} href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
      <svg viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
        <path d="M16.4 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.8-.8-3-.8-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.7 1.1 8.9.7 1.1 1.6 2.3 2.8 2.2 1.1 0 1.5-.7 2.9-.7 1.3 0 1.7.7 2.9.7 1.2 0 2-1.1 2.7-2.1.9-1.3 1.2-2.5 1.2-2.6-.1 0-2.4-.9-2.4-3.6zM14.2 5.6c.6-.8 1-1.8.9-2.9-.9 0-2 .6-2.6 1.4-.6.7-1.1 1.7-.9 2.8 1 0 2-.5 2.6-1.3z" />
      </svg>
      <span><span className="s">Download on the</span><span className="b">App Store</span></span>
    </a>
  );
}

// Dark Google Play pill. Reuses the App Store pill's dimensions (via the shared
// `appstore` class) so the two badges read as a matched pair; the Android-green
// robot is its personality counterpart to the Apple mark on the iOS pill.
function PlayStoreButton({ className = '' }) {
  return (
    <a className={`appstore playstore ${className}`} href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 18a1 1 0 001 1h1v3a1.5 1.5 0 003 0v-3h2v3a1.5 1.5 0 003 0v-3h1a1 1 0 001-1V9H6v9zM3.5 9A1.5 1.5 0 002 10.5v6a1.5 1.5 0 003 0v-6A1.5 1.5 0 003.5 9zm17 0a1.5 1.5 0 00-1.5 1.5v6a1.5 1.5 0 003 0v-6A1.5 1.5 0 0020.5 9zM15.5 4l1.3-2.3a.4.4 0 00-.7-.4L14.8 3.6a6.8 6.8 0 00-5.6 0L7.9 1.3a.4.4 0 10-.7.4L8.5 4A6 6 0 006 8.6h12A6 6 0 0015.5 4zM9.5 6.6a.8.8 0 110-1.6.8.8 0 010 1.6zm5 0a.8.8 0 110-1.6.8.8 0 010 1.6z" />
      </svg>
      <span><span className="s">GET IT ON</span><span className="b">Google Play</span></span>
    </a>
  );
}

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
    <path d="M5 12l5 5L20 7" />
  </svg>
);

// One phone-framed screenshot. If the image is missing (e.g. the Apple Watch
// shot that has not been supplied yet), fall back to a styled placeholder so
// the live site never shows a broken image.
function PhoneShot({ image, caption, sub, watch }) {
  const [errored, setErrored] = useState(false);
  const img = (
    <img
      className={watch ? 'watch-screenshot' : 'phone-screenshot'}
      src={process.env.PUBLIC_URL + '/screenshots/' + image}
      alt={caption}
      onError={() => setErrored(true)}
    />
  );

  if (watch) {
    // Apple Watch screens do not fit a phone frame, so use a watch-shaped
    // bezel centered within the same footprint as the phone tiles.
    return (
      <div className="shot shot-watch">
        <div className="watch-stage">
          {errored
            ? <div className="watch-placeholder"><span>{caption}<br />screenshot coming soon</span></div>
            : <div className="watch-frame">{img}</div>}
        </div>
        <div className="cap">{caption}<span>{sub}</span></div>
      </div>
    );
  }

  return (
    <div className="shot">
      <div className="frame">
        <div className="notch" />
        {errored
          ? <div className="shot-placeholder"><span>{caption}<br />screenshot coming soon</span></div>
          : img}
      </div>
      <div className="cap">{caption}<span>{sub}</span></div>
    </div>
  );
}

const pills = [
  { label: 'Apple Watch', icon: <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 4a2 2 0 110 4 2 2 0 010-4z" /> },
  { label: 'Detailed stats', icon: <path d="M3 13h8V3H3zm0 8h8v-6H3zm10 0h8V11h-8zm0-18v6h8V3z" /> },
  { label: 'Social feed', icon: <path d="M16 11a4 4 0 10-4-4 4 4 0 004 4zm-8 0a4 4 0 10-4-4 4 4 0 004 4z" /> },
  { label: 'Bowling Alley Directory', icon: <path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z" /> },
  { label: 'League import', icon: <path d="M3 3h18v4H3zm0 7h18v4H3zm0 7h12v4H3z" /> },
  { label: 'Average forecasting', icon: <path d="M3 17l6-6 4 4 8-8v4h-2v.6L13 18l-4-4-4.6 4.6z" /> },
  { label: 'Practice drills', icon: <path d="M12 2l3 6 6 .5-4.5 4 1.4 6L12 15.8 6.1 18.5 7.5 12.5 3 8.5 9 8z" /> },
  { label: 'Ball comparisons', icon: <path d="M8 6a3 3 0 11-3 3 3 3 0 013-3zm8 0a3 3 0 11-3 3 3 3 0 013-3zM8 13a3 3 0 11-3 3 3 3 0 013-3zm8 0a3 3 0 11-3 3 3 3 0 013-3z" /> },
  { label: 'Live score sharing', icon: <path d="M12 8a4 4 0 014 4h-2a2 2 0 00-2-2zm0-4a8 8 0 018 8h-2a6 6 0 00-6-6zm-1 9a2 2 0 11-2 2 2 2 0 012-2z" /> },
];

// Phone tiles have dark + light variants (score-dark.png / score-light.png etc.)
// resolved by the appearance toggle. The Apple Watch face is black in both
// system appearances, so its tile stays constant.
const screenshots = [
  { base: 'stats', caption: 'Stats', sub: 'Trends over time' },
  { base: 'leagues', caption: 'Leagues', sub: 'Track your season' },
  { base: 'game-summary', caption: 'Game recap', sub: 'Every game, broken down' },
  { image: 'apple-watch.png', caption: 'Apple Watch', sub: 'Score from your wrist', watch: true },
];

const features = [
  { title: 'Frame-by-frame scoring', body: 'Record every ball and keep clean, accurate frames. Splits, spares and strikes all get tracked the way you would on paper.', icon: (<><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M9 4v16" /></>) },
  { title: 'Share live stats', body: 'Send a link to friends and family so they can see your scores as you bowl.', icon: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>) },
  { title: 'Live Activities', body: 'See your scores and get back into the app from your home screen while you are bowling.', icon: (<><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M7 9h6" /></>) },
  { title: 'Apple Watch support', body: 'Score a whole game from your wrist and leave your phone in your bag at the foul line.', icon: (<><rect x="6" y="3" width="12" height="18" rx="4" /><path d="M12 7v3" /></>) },
  { title: 'Deep statistics', body: 'Track all of your stats and trends. Explore from any angle: by ball, by league, by alley and more.', icon: (<path d="M4 19V5M4 19h16M8 16V9m4 7V6m4 10v-4" />) },
  { title: 'Manage your arsenal', body: 'Keep track of every ball in your bag or on your wishlist. Updates as new balls come out.', icon: (<><circle cx="12" cy="12" r="9" /><circle cx="9.5" cy="10" r="1.2" fill="currentColor" /><circle cx="14" cy="11" r="1.2" fill="currentColor" /><circle cx="11" cy="14" r="1.2" fill="currentColor" /></>) },
  { title: 'Find any alley', body: 'Record your scores from a large and growing directory of bowling alleys across the US and internationally.', icon: (<><path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z" /><circle cx="12" cy="9" r="2.4" /></>) },
  { title: 'Social feed', body: "Follow your friends and teammates. React and comment on each other's games.", icon: (<path d="M16 11a4 4 0 10-4-4 4 4 0 004 4zM8 11a4 4 0 10-4-4 4 4 0 004 4zm0 2c-2.7 0-8 1.3-8 4v3h8m8-7a11 11 0 00-1 .1" />) },
  { title: 'Easy league import', body: 'Import your leagues and scores with just a few clicks. You can even get reminders of upcoming lane assignments.', icon: (<path d="M4 7h16M4 12h16M4 17h10" />) },
];

// Illustrative sample events for the tournaments promo. These can be wired to
// live data from Advanced Bowling Tournaments later.
const tournaments = [
  { mo: 'Jul', dy: '4', name: 'Fire Cracker 300', venue: 'Saint Lucie Lanes, FL', fee: '$60' },
  { mo: 'Apr', dy: '28', name: 'LLTC Vol.5', venue: 'Lincoln Lanes, PA', fee: '$40' },
  { mo: 'Jul', dy: '2', name: 'Firecracker Handicap Singles', venue: 'Astro Superbowl, San Antonio, TX', fee: '$80' },
];

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [appearance, setAppearance] = useState('dark');
  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <div className="app">
      {/* Announcement bar */}
      <div className="announce">
        <AppLogo className="miniball" />
        <span>Get your first month of Pro free.</span>
        <a href={STORE_URL} target="_blank" rel="noopener noreferrer">{STORE_LABEL}</a>
      </div>

      {/* Nav */}
      <nav>
        <div className="wrap nav-in">
          <a href="/" className="logo"><AppLogo className="ball" /> Advanced Bowling Tracker</a>
          <div className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>
            <a href="#features" onClick={closeMenu}>Features</a>
            <a href="#screens" onClick={closeMenu}>Screenshots</a>
            <a href="#tournaments" onClick={closeMenu}>Tournaments</a>
            <a href="#pricing" onClick={closeMenu}>Pricing</a>
            <a href="/guides">Guides</a>
            <a href="/whats-new">What's New</a>
            <a className="btn btn-red btn-sm" href={STORE_URL} target="_blank" rel="noopener noreferrer">Download</a>
          </div>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            <span></span><span></span><span></span>
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="wrap">
          <h1>Detailed bowling tracking <span className="accent">for everyone.</span></h1>
          <p className="sub">Keep score frame by frame, import your past scores and track your entire arsenal. ABT is built for league and tournament bowlers, or anyone that wants to improve their bowling.</p>
          <div className="cta-row">
            <AppStoreButton />
            <PlayStoreButton />
          </div>

          <div className="device-stage">
            <div className="glow"></div>
            <div className="phone">
              <div className="notch"></div>
              <img className="phone-screenshot" src={process.env.PUBLIC_URL + '/screenshots/score-' + appearance + '.png'} alt="Frame-by-frame scoring" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="strip">
        <div className="wrap">
          <p>Everything in one app</p>
          <div className="strip-items">
            {pills.map((pill) => (
              <span className="pill" key={pill.label}>
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{pill.icon}</svg> {pill.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshot showcase */}
      <section className="sec" id="screens">
        <div className="wrap">
          <div className="sec-head">
            <h2>See your game from every angle</h2>
            <p>From the scoring screen to your season stats, here is what bowling with the app actually looks like.</p>
            <div className="appearance-toggle" role="group" aria-label="Preview appearance">
              <button type="button" className={appearance === 'dark' ? 'active' : ''} onClick={() => setAppearance('dark')} aria-pressed={appearance === 'dark'}>
                <svg className="toggle-ico" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" /></svg>
                Dark
              </button>
              <button type="button" className={appearance === 'light' ? 'active' : ''} onClick={() => setAppearance('light')} aria-pressed={appearance === 'light'}>
                <svg className="toggle-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.4 1.4M17.6 17.6L19 19M19 5l-1.4 1.4M6.4 17.6L5 19" /></svg>
                Light
              </button>
            </div>
          </div>
          <div className="shots">
            {screenshots.map((s) => (
              <PhoneShot key={s.caption} image={s.watch ? s.image : `${s.base}-${appearance}.png`} caption={s.caption} sub={s.sub} watch={s.watch} />
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="sec sec-alt" id="features">
        <div className="wrap">
          <div className="sec-head">
            <div className="k">Features</div>
            <h2>Explore what makes ABT special</h2>
          </div>
          <div className="fgrid">
            {features.map((f, i) => (
              <div className="card" key={f.title}>
                <div className="frame-head">
                  <span className="frame-num">{i + 1}</span>
                  <span className="frame-ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">{f.icon}</svg>
                  </span>
                </div>
                <div className="card-body">
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tournaments promo */}
      <section className="sec" id="tournaments">
        <div className="wrap">
          <div className="promo">
            <div>
              <div className="k">Advanced Bowling Tournaments</div>
              <h2>Find your next tournament</h2>
              <p>Browse hundreds of upcoming tournaments across the US, register online, or run your own for free.</p>
              <a className="btn btn-red" href={TOURNAMENTS_URL} target="_blank" rel="noopener noreferrer">Visit Advanced Bowling Tournaments</a>
            </div>
            <div className="tlist">
              <div className="tstat"><b>1,000+</b> upcoming tournaments</div>
              {tournaments.map((t) => (
                <div className="tcard" key={t.name}>
                  <div className="tdate"><span className="mo">{t.mo}</span><span className="dy">{t.dy}</span></div>
                  <div className="tinfo">
                    <div className="tname">{t.name}</div>
                    <div className="tmeta">{t.venue}</div>
                  </div>
                  <div className="fee">{t.fee}<small>Entry</small></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="sec sec-alt" id="pricing">
        <div className="wrap">
          <div className="sec-head"><div className="k">Pricing</div><h2>Start free. Try Pro free for 1 month.</h2></div>
          <div className="price-grid">
            <div className="plan">
              <h3>Free</h3>
              <div className="amt">$0</div>
              <div className="note">Everything you need to get started</div>
              <div className="feat">
                <div><Check /> Unlimited score recording</div>
                <div><Check /> Frame by frame scoring for 15 games a month</div>
                <div><Check /> Try one drill per month</div>
                <div><Check /> Stats, social feed and leagues</div>
              </div>
              <a className="btn btn-ghost" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">Download free</a>
            </div>
            <div className="plan pro">
              <span className="badge">1 month free</span>
              <h3 className="pro-title">Pro</h3>
              <div className="amt">$9.99<small> / year</small></div>
              <div className="note">First month free, then $9.99 a year or $1.99 a month</div>
              <div className="feat">
                <div><Check /> Unlimited frame-by-frame scoring</div>
                <div><Check /> Unlimited drill sessions</div>
                <div><Check /> Advanced ball comparison tools</div>
                <div><Check /> Average improvement forecasting</div>
              </div>
              <a className="btn btn-red" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">Sign up in the app</a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="wrap">
          <div className="foot">
            <div>
              <div className="logo"><AppLogo className="ball" /> Advanced Bowling Tracker</div>
              <p>&copy; {new Date().getFullYear()} Joseph Miller.</p>
            </div>
            <div>
              <h4>App</h4>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#screens">Screenshots</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="/guides">Guides</a></li>
                <li><a href="/whats-new">What's New</a></li>
                <li><a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">iOS app</a></li>
                <li><a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">Android app</a></li>
              </ul>
            </div>
            <div>
              <h4>More</h4>
              <ul>
                <li><a href={TOURNAMENTS_URL} target="_blank" rel="noopener noreferrer">Tournaments</a></li>
                <li><a href={SUPPORT_EMAIL}>Support</a></li>
                <li><a href={SUPPORT_EMAIL}>Contact</a></li>
              </ul>
            </div>
            <div>
              <h4>Legal</h4>
              <ul>
                <li><a href="/privacy">Privacy Policy</a></li>
                <li><a href="/terms">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="copyright">
            <span>Made for bowlers, by bowlers.</span>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">Download on the App Store</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default DEEP_LINK_PATH ? DeepLinkInterstitial : App;
